import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Grid,
  List,
  ListItem,
  Divider,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery, useTheme } from "@mui/material";
import "./CookingInterface.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useWakeLock } from "react-screen-wake-lock";
import { useSearchParams } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

const LONG_INGREDIENT_LIST_LENGTH = 9;

const CookingInterface = ({ recipe }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { id, step: stepParam } = useParams();
  const [searchParams] = useSearchParams();
  const urlServings = searchParams.get("servings");
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(parseInt(stepParam, 10) || 0);

  const { request, release } = useWakeLock();

  useEffect(() => {
    request();

    return () => {
      release();
    };
  }, [release, request]);

  const buttonStyles = {
    padding: {
      xs: "10px 20px", // for extra small devices
      sm: "12px 24px", // for small devices
      md: "14px 28px", // for medium devices
      lg: "16px 32px", // for large devices
    },
    fontSize: {
      xs: "0.8rem", // for extra small devices
      sm: "0.9rem", // for small devices
      md: "1rem", // for medium devices
      lg: "1.1rem", // for large devices
    },
    // Add other style adjustments as needed
  };

  const ingredientsContent =
    currentStep === 0 ||
    (recipe?.steps?.[currentStep - 1]?.show_ingredients_table &&
      recipe.steps[currentStep - 1]?.ingredients?.length !== 0);
  const currentInstruction =
    recipe?.steps?.[currentStep - 1]?.instruction || "";
  const stepsContent = currentStep > 0 && !!currentInstruction;

  // Effect to handle initial load or direct navigation to a specific step
  useEffect(() => {
    const stepFromUrl = parseInt(stepParam, 10);
    if (!isNaN(stepFromUrl)) {
      setCurrentStep(stepFromUrl);
    }
  }, [stepParam]);

  // Function to navigate to the next step
  const goToStep = (stepNumber) => {
    setCurrentStep(stepNumber);
    navigate(`/recipe/${id}/cooking/${stepNumber}${location.search}`, {
      replace: false,
    });
  };

  // Example handlers for next/previous buttons
  const handleNextStep = () => {
    if (currentStep < recipe.steps.length) {
      goToStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const speak = (text) => {
    const synth = window.speechSynthesis;
    const utterance = new SpeechSynthesisUtterance(text);
    synth.speak(utterance);
  };

  useEffect(() => {
    if (
      currentStep > 0 &&
      currentStep <= (recipe?.steps?.length || 0) &&
      currentInstruction
    ) {
      speak(recipe.steps[currentStep - 1].instruction);
    }
  }, [currentInstruction, currentStep, recipe?.steps]);

  // Function to compile adjusted ingredients for the current step
  const getCurrentStepIngredients = () => {
    // Check if recipe is defined and has steps
    if (!recipe || !recipe.steps || recipe.steps.length === 0) {
      console.log("No current recipe or steps found");
      return [];
    }

    // If we're looking at the initial step (e.g., a summary of all ingredients)
    if (currentStep === 0) {
      // Assuming all ingredients are summarized in a property at the root of recipe
      // Ensure that this property exists and is structured as expected
      return recipe.steps.flatMap((step) =>
        (step.ingredients || []).map((ingredient) => ({
          amount: ingredient.formattedAmount || ingredient.amount || "",
          unit: ingredient.unit?.name || "",
          name:
            ingredient.food?.name || ingredient.name || "Unnamed ingredient",
          note: ingredient.note,
        }))
      );
    } else {
      // For specific steps, ensure that currentStep is within the bounds of the steps array
      if (currentStep - 1 < recipe.steps.length) {
        return (recipe.steps[currentStep - 1].ingredients || []).map(
          (ingredient) => ({
            amount: ingredient.formattedAmount || ingredient.amount || "",
            unit: ingredient.unit?.name || "",
            name:
              ingredient.food?.name ||
              ingredient.name ||
              "Unnamed ingredient",
            note: ingredient.note,
          })
        );
      } else {
        console.log("Current step is out of bounds");
        return [];
      }
    }
  };

  const onClose = () => {
    release();
    navigate(`/recipe/${id}?servings=${urlServings}`);
  };

  // Initialize tab value based on device type
  const [tabValue, setTabValue] = useState(0);
  const ingredientsListRef = useRef(null);
  const [hasMoreIngredients, setHasMoreIngredients] = useState(false);

  useEffect(() => {
    const updateOverflowCue = () => {
      const list = ingredientsListRef.current;
      if (!list) {
        setHasMoreIngredients(false);
        return;
      }

      setHasMoreIngredients(
        list.scrollTop + list.clientHeight < list.scrollHeight - 2
      );
    };

    const frame = window.requestAnimationFrame(updateOverflowCue);
    const resizeObserver = window.ResizeObserver
      ? new window.ResizeObserver(updateOverflowCue)
      : null;

    if (ingredientsListRef.current) {
      resizeObserver?.observe(ingredientsListRef.current);
    }
    window.addEventListener("resize", updateOverflowCue);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateOverflowCue);
    };
  }, [currentStep, recipe?.steps]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Update tab value when currentStep changes
  useEffect(() => {
    if (isMobile) {
      setTabValue(currentStep === 0 ? 0 : 1);
    }
  }, [currentStep, isMobile]);

  if (!recipe) {
    return (
      <div className="cooking-interface-loading">
        <CircularProgress size={96} thickness={2} />
      </div>
    );
  }

  const renderIngredientsSection = () => {
    const stepIngredientsList = getCurrentStepIngredients();
    const isLongIngredientList =
      stepIngredientsList.length >= LONG_INGREDIENT_LIST_LENGTH;

    return (
      <Grid
        item
        xs={12}
        md={ingredientsContent && stepsContent ? 6 : 12}
        className={`cooking-section ingredients-section ${
          currentStep === 0
            ? "mise-en-place-section"
            : "step-ingredients-section"
        }`}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "flex-start",
          textAlign: "left",
        }}
      >
        <Box className="cooking-section-heading">
          <Box>
            <Typography variant="h5" component="h2" fontWeight={600}>
              {currentStep === 0 ? "Mise en place" : "Step ingredients"}
            </Typography>
          </Box>
          <Typography
            className="ingredient-count"
            variant="body2"
            color="text.secondary"
          >
            {stepIngredientsList.length}{" "}
            {stepIngredientsList.length === 1 ? "ingredient" : "ingredients"}
          </Typography>
        </Box>
        <Divider className="cooking-section-divider" />
        <Box className="mise-en-place-scroll-shell">
          <Box
            className="mise-en-place-container"
            ref={ingredientsListRef}
            role="region"
            aria-label={
              currentStep === 0
                ? "Mise en place ingredients"
                : "Step ingredients"
            }
            tabIndex={0}
            onScroll={(event) => {
              const { scrollTop, scrollHeight, clientHeight } =
                event.currentTarget;
              setHasMoreIngredients(
                scrollTop + clientHeight < scrollHeight - 2
              );
            }}
          >
            {stepIngredientsList.length > 0 ? (
              <List
                className={`mise-en-place-list ${
                  isLongIngredientList ? "is-long" : ""
                }`}
                disablePadding
              >
                {stepIngredientsList.map((ingredient, index) => (
                  <ListItem
                    key={index}
                    className="mise-en-place-item"
                    disableGutters
                  >
                    <Typography
                      component="span"
                      className="ingredient-measure"
                    >
                      {[ingredient.amount, ingredient.unit]
                        .filter(Boolean)
                        .join(" ")}
                    </Typography>
                    <Box className="ingredient-details">
                      <Typography
                        component="span"
                        className="ingredient-name"
                      >
                        {ingredient.name}
                      </Typography>
                      {ingredient.note && (
                        <Typography
                          component="span"
                          className="ingredient-note"
                          color="text.secondary"
                        >
                          {ingredient.note}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                className="ingredients-empty"
                color="text.secondary"
              >
                No ingredients listed for this step.
              </Typography>
            )}
          </Box>
          {hasMoreIngredients && (
            <div className="mise-en-place-scroll-cue" aria-hidden="true" />
          )}
        </Box>
      </Grid>
    );
  };

  const renderStepsSection = () => (
    <Grid
      item
      xs={12}
      md={ingredientsContent && stepsContent ? 6 : 12}
      className="cooking-section instructions-section"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        textAlign: "left",
      }}
    >
      <div className="step-content-heading">Step Instructions</div>
      <Divider className="cooking-section-divider" />

      <Box className="instruction-scroll-container">
        <div className="step-content">{currentInstruction}</div>
      </Box>
    </Grid>
  );

  return (
    <Card
      className="cooking-interface-card"
      sx={{
        display: "flex",
        flexDirection: "column", // Stacks children (CardContent) vertically
        minWidth: 275,
        width: isDesktop ? "90vw" : "auto",
        margin: isDesktop ? "auto" : "16px",
        marginBottom: "16px",
        height: isMobile ? "calc(100vh - 36px)" : "calc(100vh - 117px)",
      }}
    >
      <CardContent>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            {isDesktop && (
              <Avatar
                src={recipe.image || "/img/default.png"}
                alt={recipe.name}
                sx={{ width: 120, height: 120, borderRadius: "10%" }}
              />
            )}
          </Grid>
          <Grid item xs>
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: 600, textAlign: "left", px: 2 }}
            >
              {currentStep === 0
                ? `${recipe.name} - All Ingredients`
                : recipe.steps[currentStep - 1]?.name
                ? `${recipe.name} - Step ${currentStep} of ${recipe.steps.length}: ${recipe.steps[currentStep - 1].name}`
                : `${recipe.name} - Step ${currentStep} of ${recipe.steps.length}`}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </CardContent>

      <Divider variant="middle" />

      {isMobile && (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Ingredients and Steps Tabs"
            variant="fullWidth" // Ensures the tabs take up the full width
            sx={{
              ".MuiTabs-flexContainer": {
                display: "flex",
                justifyContent: "space-between",
              },
            }}
          >
            {ingredientsContent && (
              <Tab
                label="Ingredients"
                value={0}
                disabled={!ingredientsContent}
                sx={{ flexGrow: 1 }}
              />
            )}
            <Tab
              label="Steps"
              value={1}
              disabled={!stepsContent}
              sx={{ flexGrow: 1 }}
            />
          </Tabs>
        </Box>
      )}

      <CardContent
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "hidden",
          alignItems: "center", // This is important for vertical centering
          justifyContent: "center", // And this for horizontal centering
        }}
      >
        <Grid
          className="step-content-container"
          container
          spacing={2}
          direction={ingredientsContent && stepsContent ? "row" : "column"}
          alignItems="center"
          justifyContent="center"
        >
          {isMobile ? (
            <>
              <TabPanel value={tabValue} index={0}>
                {ingredientsContent && renderIngredientsSection()}
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                {stepsContent && renderStepsSection()}
              </TabPanel>
            </>
          ) : (
            <>
              {ingredientsContent && renderIngredientsSection()}
              {stepsContent && renderStepsSection()}
            </>
          )}
        </Grid>
      </CardContent>

      <CardContent
        sx={{
          flex: "0 0 auto",
          textAlign: "center",
          background: "transparent",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
          <Button
            onClick={handlePreviousStep}
            disabled={currentStep === 0}
            variant="contained"
            color="primary"
            sx={buttonStyles}
          >
            Previous
          </Button>

          <Button
            onClick={handleNextStep}
            disabled={currentStep === recipe.steps.length}
            variant="contained"
            color="primary"
            sx={buttonStyles}
          >
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className="cooking-tab-panel"
      {...other}
    >
      {value === index && (
        <Box className="cooking-tab-panel-content">{children}</Box>
      )}
    </div>
  );
}
export default CookingInterface;
